import React, { useState, useEffect, useRef } from 'react';
import Card from '@/components/ui/cards/Card';
import CardHeader from '@/components/ui/cards/CardHeader';
import CardBody from '@/components/ui/cards/CardBody';
import { InputDatepicker } from '@/components/ui/inputs/input-datepicker';
import { getSalesService } from '@/services/DashboardServices';
import validationErrors from '@/lib/validationErrors';
import { useSelector, useDispatch } from 'react-redux';
import CurrencyCodeAdd from '@/components/common/CurrencyCodeAdd';
import { Line, Bar } from 'react-chartjs-2';
import { Chart as ChartJS, LineElement, BarElement, PointElement, CategoryScale, LinearScale, Tooltip, Legend, Title } from 'chart.js';

ChartJS.register(LineElement, BarElement, PointElement, CategoryScale, LinearScale, Tooltip, Legend, Title);

const SalesSummaryComponent = () => {
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(false);
    const [dataSource, setDataSource] = useState({});
    const today = new Date();
    const oneMonthFromToday = new Date();
    oneMonthFromToday.setMonth(today.getMonth() - 1);
    const [dateRangeSelect, setDateRangeSelect] = useState({ start_date: oneMonthFromToday, end_date: today });
    const [chartData, setChartData] = useState({
        labels: [], // Dynamic labels
        datasets: [
            {
                label: 'Sales',
                data: [], // Dynamic data
                fill: false,
                borderColor: 'red',
                tension: 0.1, // Smoothness of the curve
            },
        ],
    });

    const options = {
        responsive: true,
        plugins: {
            legend: {
                position: 'top',
            },
            title: {
                display: true,
                text: 'Sales Report',
            },
        },
    };

    const handleDateChange = (dates) => {
        const [start, end] = dates;
        setDateRangeSelect({ start_date: start, end_date: end });
    };

    useEffect(() => {
        if (dateRangeSelect?.start_date && dateRangeSelect?.end_date) {
            getSalesData(dateRangeSelect);
        }
    }, [dateRangeSelect?.start_date, dateRangeSelect?.end_date]);

    const getSalesData = (data) => {
        setIsLoading(true);
        dispatch(getSalesService(data))
            .then((res) => {
                const salesData = res?.data?.data || {};
                setDataSource(salesData);
                
                // Generate labels array
                const labels = [];
                const startDate = new Date(dateRangeSelect?.start_date);
                const endDate = new Date(dateRangeSelect?.end_date);
                const currentDate = new Date(startDate);

                // Reset time to avoid timezone issues
                currentDate.setHours(0, 0, 0, 0);
                endDate.setHours(23, 59, 59, 999);

                while (currentDate <= endDate) {
                    const formattedDate = currentDate.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' }).replace(/\//g, '-'); // Formats as "DD-MM"
                    labels.push(formattedDate);
                    currentDate.setDate(currentDate.getDate() + 1); // Increment by 1 day
                }
                
                const dynamicLabels = labels;
                const dynamicData = salesData?.per_day_sales || [];
                
                // Ensure data arrays match in length
                const paddedData = dynamicData.length < labels.length 
                    ? [...dynamicData, ...Array(labels.length - dynamicData.length).fill(0)]
                    : dynamicData.slice(0, labels.length);
                
                setChartData({
                    labels: dynamicLabels,
                    datasets: [
                        {
                            label: 'Sales',
                            data: paddedData,
                            fill: false,
                            borderColor: 'red',
                            tension: 0.1, // Smoothness of the curve
                        },
                    ],
                });
                setIsLoading(false);
            })
            .catch(({ message, errorData, statusCode }) => {
                validationErrors(errorData, message);
                setIsLoading(false);
            });
    };

    return (
        <Card>
            <CardHeader isBorder={true}>
                <h3 className="db-card-title">Sales Summary</h3>
                <InputDatepicker
                    iconClassName="flex items-center me-3"
                    selected={dateRangeSelect?.start_date}
                    onChange={handleDateChange}
                    startDate={dateRangeSelect?.start_date}
                    endDate={dateRangeSelect?.end_date}
                    selectsRange
                    suffixIcon={<i className="lab lab-calendar lab-font-size-24 lab-color-pink"></i>}
                    size="sm"
                />
            </CardHeader>
            <CardBody>
                {isLoading ? (
                    <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                    </div>
                ) : (
                    <>
                        <ul className="flex gap-11">
                            <li>
                                <div className="flex items-center gap-2.5">
                                    <i className="lab lab-sale-summary lab-font-size-20 lab-font-color-2"></i>
                                    <h3 className="font-bold text-[22px] leading-[34px]">
                                        <CurrencyCodeAdd price={dataSource?.total_sales ?? 0} />
                                    </h3>
                                </div>
                                <p className="text-xs capitalize">Total Sales</p>
                            </li>
                            <li>
                                <div className="flex items-center gap-2.5">
                                    <i className="lab lab-sale-summary lab-font-size-20 lab-font-color-2"></i>
                                    <h3 className="font-bold text-[22px] leading-[34px]">
                                        <CurrencyCodeAdd price={dataSource?.avg_per_day ?? 0} />
                                    </h3>
                                </div>
                                <p className="text-xs capitalize">Avg Sales Per Day</p>
                            </li>
                        </ul>
                        <div id="area-chart">
                            {chartData.labels.length > 0 ? (
                                <Line data={chartData} options={options} />
                            ) : (
                                <div className="flex items-center justify-center py-8 text-gray-500">
                                    <p>No sales data available for the selected date range</p>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </CardBody>
        </Card>
    );
};

export default SalesSummaryComponent;
